/**
 * 数值公差 → 公差档位 的确定性归档(纯函数 · 与后端 TOLERANCE_FINISH_FACTOR 四档对齐)。
 *
 * 归档阈值与图纸预填器(drawing_prefill)的规则同源:
 *   ≤±0.01 → 超精(IT5-6) · ≤±0.05 → 精密(IT7-9) · ≤±0.2 → 标准(GB1804-m/IT10-12)
 *   · 其余 → 粗(未注公差/GB1804-c)。
 * 仅作输入辅助:报价按档位算(快速报价拿不到逐特征工艺规划 · 四档是诚实粒度),
 * 数值本身不进后端。
 */

export type ToleranceTier = "coarse" | "standard" | "precision" | "ultra";

export const TIER_OPTIONS: { value: ToleranceTier; label: string }[] = [
  { value: "coarse", label: "粗" },
  { value: "standard", label: "标准" },
  { value: "precision", label: "精密" },
  { value: "ultra", label: "超精" },
];

export const TIER_LABEL: Record<ToleranceTier, string> = {
  coarse: "粗",
  standard: "标准",
  precision: "精密",
  ultra: "超精",
};

/** 每档对应的典型图纸标注(hint 展示 · 与产品文案一致)。 */
export const TIER_RANGE_LABEL: Record<ToleranceTier, string> = {
  ultra: "±0.01 内 · IT5-6",
  precision: "±0.02~0.05 · IT7-9",
  standard: "±0.1 级 · IT10-12 / GB1804-m",
  coarse: "±0.3 起 · 未注公差 / GB1804-c",
};

/** 每档一句人话范围(总纲 §5.1 G1 公差档:粗(未注公差)/ 标准(±0.1 级)/ 精密(±0.02~0.05)/ 超精(±0.01 内))。 */
export const TIER_PLAIN_RANGE: Record<ToleranceTier, string> = {
  coarse: "未注公差",
  standard: "±0.1 级",
  precision: "±0.02~0.05",
  ultra: "±0.01 内",
};

function isTier(value: unknown): value is ToleranceTier {
  return typeof value === "string" && Object.hasOwn(TIER_LABEL, value);
}

/** 公差档的叫法(全模块只用这一份)· 认不出的值不原样露代码,写「精度待确认」。 */
export function toleranceTierLabel(value: unknown): string {
  return isTier(value) ? TIER_LABEL[value] : "精度待确认";
}

/** 带范围的叫法 · 如「标准（±0.1 级）」。 */
export function toleranceTierText(value: unknown): string {
  return isTier(value)
    ? `${TIER_LABEL[value]}（${TIER_PLAIN_RANGE[value]}）`
    : "精度待确认";
}

/** 档位由宽到严的次序(粗 0 · 标准 1 · 精密 2 · 超精 3)· 比较「哪个更严」用。 */
export function tierRank(t: ToleranceTier): number {
  return TIER_OPTIONS.findIndex((o) => o.value === t);
}

/** "±0.05" / "0.05mm" / " 0.05 " → 0.05;非法/非正数 → null。 */
export function parseToleranceMm(text: string): number | null {
  const m = /^±?\s*(\d*\.?\d+)\s*(?:mm)?$/i.exec(text.trim());
  if (!m) return null;
  const v = Number.parseFloat(m[1]);
  return Number.isFinite(v) && v > 0 ? v : null;
}

/** 每档代表 ±mm 值(点档位按钮 → 回填 mm 输入 · 双向联动)· 与归档阈值一致。 */
export const TIER_REPRESENTATIVE_MM: Record<ToleranceTier, number> = {
  ultra: 0.01,
  precision: 0.05,
  standard: 0.1,
  coarse: 0.3,
};

/** 数值公差(±mm)→ 档位 · 阈值含边界(≤)。 */
export function tierForToleranceMm(tMm: number): ToleranceTier {
  if (tMm <= 0.01) return "ultra";
  if (tMm <= 0.05) return "precision";
  if (tMm <= 0.2) return "standard";
  return "coarse";
}

/** 归档依据文案 · 如 "±0.02 → 精密(±0.02~0.05 · IT7-9)"。 */
export function tierHint(tMm: number, tier: ToleranceTier): string {
  return `±${tMm} → ${TIER_LABEL[tier]}(${TIER_RANGE_LABEL[tier]})`;
}
